<template>
    <div>
        <template
            v-for="(productChange, index) in sortedProductChanges"
            :key="productChange.id || index"
        >
            <div
                class="flex flex-col items-start p-4 rounded-xl bg-slate-50 dark:bg-slate-700/50 relative mb-4"
            >
                <!-- Card Details Section Header with actions -->
                <div class="flex justify-between items-center w-full mb-4">
                    <h5 class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100">
                        {{
                            index === sortedProductChanges.length - 1
                                ? 'Original card details'
                                : 'Previous card details'
                        }}
                    </h5>
                    <div class="flex gap-1">
                        <button
                            type="button"
                            @click="$emit('edit', productChange, index)"
                            class="p-1 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 rounded"
                        >
                            <Icon icon="pencil" :width="14" :height="14" />
                        </button>
                        <button
                            type="button"
                            @click="openDeleteConfirmation(index)"
                            class="p-1 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 rounded"
                        >
                            <Icon icon="trash" :width="14" :height="14" />
                        </button>
                    </div>
                </div>

                <!-- Card Info -->
                <div class="flex items-center gap-6 w-full mb-4">
                    <div class="flex items-center flex-1">
                        <span
                            class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                        >
                            {{
                                index === sortedProductChanges.length - 1
                                    ? 'Original card'
                                    : 'Previous card'
                            }}
                        </span>
                    </div>
                    <div class="flex flex-col items-start gap-2 flex-1">
                        <div class="w-auto h-5">
                            <img
                                v-if="productChange.previous_card?.card_image"
                                :src="`${cdnUrl}/${productChange.previous_card.card_image?.path}/_small/${productChange.previous_card.card_image?.file_name}`"
                                class="h-5 w-auto object-contain rounded-sm"
                                alt="Original card image"
                            />
                            <Icon
                                v-else
                                icon="credit-card"
                                :width="32"
                                :height="20"
                            />
                        </div>
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                        >
                            {{
                                productChange.previous_card?.title ||
                                '(Card not found)'
                            }}
                        </span>
                    </div>
                </div>

                <!-- Date Opened - Only show for the oldest product change (last in reversed list) -->
                <div
                    v-if="index === sortedProductChanges.length - 1"
                    class="flex items-center gap-6 w-full mb-4"
                >
                    <div class="flex items-center flex-1">
                        <span
                            class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                        >
                            Date opened
                        </span>
                    </div>
                    <div class="flex flex-1">
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                        >
                            {{
                                productChange.product_changed_date_opened
                                    ? formatDate(
                                          productChange.product_changed_date_opened
                                      )
                                    : '(Add original open date)'
                            }}
                        </span>
                    </div>
                </div>

                <!-- Date Product Change -->
                <div class="flex items-center gap-6 w-full">
                    <div class="flex items-center flex-1">
                        <span
                            class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                        >
                            Date product changed
                        </span>
                    </div>
                    <div class="flex flex-1">
                        <span
                            class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                        >
                            {{
                                productChange.product_changed_date
                                    ? formatDate(
                                          productChange.product_changed_date
                                      )
                                    : '(Add product change date)'
                            }}
                        </span>
                    </div>
                </div>

                <!-- Original Welcome Bonus - Inside the oldest product change container -->
                <div
                    v-if="index === sortedProductChanges.length - 1"
                    class="mt-6 w-full"
                >
                    <h5
                        class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100 mb-3"
                    >
                        Original welcome bonus
                    </h5>
                    <WelcomeBonusForm
                        v-model:welcome-bonus="welcomeBonus"
                        v-model:tiered-bonuses="tieredBonuses"
                        :errors="errors"
                        :backgroundColor="'white'"
                        @delete="$emit('reset-welcome-bonus')"
                    />
                </div>
            </div>
        </template>

        <!-- Add Another Button -->
        <button
            type="button"
            class="btn-base btn-transparent !pl-0 flex items-center"
            @click.stop="$emit('add')"
        >
            <Icon icon="plus" class="mr-1" />
            Add another product change
        </button>

        <Teleport to="body">
            <ConfirmationDialog
                :show="showDeleteConfirmation"
                title="Delete product change"
                dialog="Are you sure you want to delete this product change? This action cannot be undone."
                icon="trash"
                iconColor="red"
                @cancel="cancelDelete"
                @confirm="confirmDelete"
                :buttons="[
                    { type: 'cancel', text: 'Cancel' },
                    {
                        type: 'confirm',
                        text: 'Delete',
                        value: true,
                        classes: '!bg-red-500 !text-white hover:!bg-red-600',
                    },
                ]"
            />
        </Teleport>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import ConfirmationDialog from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';
import WelcomeBonusForm from '@/components/wallet/partials/welcome-bonus-form.vue';
import { Apii } from '@/services/api.js';

const ApiService = new Apii();

const emit = defineEmits([
    'delete',
    'deleted',
    'add',
    'edit',
    'reset-welcome-bonus',
]);

const productChanges = defineModel('product-changes', {
    type: Array,
});

const welcomeBonus = defineModel('welcome-bonus', {
    type: Object,
    default: () => ({}),
});

const tieredBonuses = defineModel('tiered-bonuses', {
    type: Array,
    default: () => [],
});

const props = defineProps({
    errors: Object,
    formBackgroundColor: { type: String, default: 'slate' },
    connectedAccountId: { type: [Number, String], default: null },
});

const cdnUrl = window.cdnUrl;

const showDeleteConfirmation = ref(false);
const pendingDeleteIndex = ref(null);

const openDeleteConfirmation = (index) => {
    pendingDeleteIndex.value = index;
    showDeleteConfirmation.value = true;
};

const cancelDelete = () => {
    showDeleteConfirmation.value = false;
    pendingDeleteIndex.value = null;
};

const confirmDelete = () => {
    if (pendingDeleteIndex.value !== null) {
        removeProductChange(pendingDeleteIndex.value);
    }
    showDeleteConfirmation.value = false;
    pendingDeleteIndex.value = null;
};

const sortedProductChanges = computed(() => {
    if (!productChanges.value || productChanges.value.length === 0) return [];
    return [...productChanges.value].sort((a, b) => {
        const dateA = a.product_changed_date
            ? new Date(a.product_changed_date)
            : null;
        const dateB = b.product_changed_date
            ? new Date(b.product_changed_date)
            : null;
        const timeA = dateA ? dateA.getTime() : null;
        const timeB = dateB ? dateB.getTime() : null;
        const validA = timeA !== null && !Number.isNaN(timeA);
        const validB = timeB !== null && !Number.isNaN(timeB);
        if (!validA && !validB) return 0;
        if (!validA) return 1;
        if (!validB) return -1;
        return timeB - timeA;
    });
});

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const createDefaultProductChange = () => ({
    previous_card_id: null,
    product_changed_date: null,
    product_changed_date_opened: null,
});

const removeProductChange = async (sortedIndex) => {
    if (!productChanges.value || productChanges.value.length === 0) {
        return;
    }

    // Rows render in date-sorted order, so map the sorted index back to the row
    const productChange = sortedProductChanges.value[sortedIndex];

    // Product changes are persisted as they are added, so removal has to
    // delete from the backend too. Keep the row if the delete fails.
    let freshConnectedAccount = null;
    if (productChange?.id && props.connectedAccountId) {
        try {
            const apiResponse = await ApiService.apiCall(
                `/wallets/cards/${props.connectedAccountId}/product-changes/${productChange.id}`,
                'delete'
            );
            freshConnectedAccount = apiResponse?.data?.connected_account || null;
        } catch (error) {
            console.error('Error deleting product change:', error);
            return;
        }
    }

    if (productChanges.value.length === 1) {
        productChanges.value[0] = createDefaultProductChange();
        emit('delete', freshConnectedAccount);
    } else {
        // Find the actual index in the original array
        const originalIndex = productChanges.value.findIndex(
            (pc) => pc === productChange || (pc.id && pc.id === productChange.id)
        );
        if (originalIndex !== -1) {
            productChanges.value.splice(originalIndex, 1);
        }

        // Deleting the newest change makes the backend restore the previous
        // card on the account — let the parent adopt the fresh payload.
        emit('deleted', freshConnectedAccount);
    }
};
</script>
